import React from 'react';

import type { GameState } from '../../types/game';
import { eventStore } from '../../services/EventStore';
import { clearReservedPlayerId, getSessionPlayerId, setSessionPlayerId } from '../../utils/sessionPlayer';
import { loadGroupPlayer } from '../../utils/groupPersistence';
import { isVerboseLoggingEnabled, logger } from '../../utils/logger';
import { readSession } from '../../utils/sessionContract';

type RuntimeWindow = Window & {
  __CLIENT_ID?: string | null;
  __DEBUG_ADOPT_ALL_PLAYERS__?: boolean;
};

export function usePlayerAdoption({
  gameState,
  modeHint,
  currentClientPlayerId,
  setCurrentClientPlayerId,
  setIsHost,
  isHost,
  joinGame,
  log,
}: {
  gameState: GameState;
  modeHint?: GameState['mode'];
  currentClientPlayerId: string | null;
  setCurrentClientPlayerId: (value: string | null) => void;
  setIsHost: (value: boolean) => void;
  isHost: boolean;
  joinGame: (
    gameId: string,
    options?: { groupToken?: string | null; skipGroupLookup?: boolean; assumeHost?: boolean },
  ) => Promise<boolean>;
  log: (label: string, payload: unknown) => void;
}) {
  const autoJoinAttemptRef = React.useRef<{ roomId: string; inFlight: boolean } | null>(null);

  React.useEffect(() => {
    if (modeHint === 'REAL') return;
    if (gameState.mode !== 'REAL') return;
    if (gameState.players.length > 0) return;
    let targetRoomId: string | null = null;
    try {
      const session = readSession();
      targetRoomId = session?.mode === 'ONLINE' ? session.gameId : null;
    } catch {
      targetRoomId = null;
    }
    if (!targetRoomId) return;
    if (autoJoinAttemptRef.current?.inFlight) return;
    if (autoJoinAttemptRef.current?.roomId === targetRoomId) return;
    autoJoinAttemptRef.current = { roomId: targetRoomId, inFlight: true };
    log('autoJoin:attempt', { targetRoomId });
    void joinGame(targetRoomId)
      .then((success) => {
        if (!success) {
          autoJoinAttemptRef.current = null;
          return;
        }
        autoJoinAttemptRef.current = { roomId: targetRoomId as string, inFlight: false };
      })
      .catch(() => {
        autoJoinAttemptRef.current = null;
      });
  }, [gameState.mode, gameState.players.length, joinGame, log, modeHint]);

  React.useEffect(() => {
    if (gameState.mode === 'ONLINE' || gameState.players.length > 0) {
      autoJoinAttemptRef.current = null;
    }
  }, [gameState.mode, gameState.players.length]);

  React.useEffect(() => {
    if (typeof window === 'undefined' || !isVerboseLoggingEnabled()) return;
    (window as RuntimeWindow).__CLIENT_ID = currentClientPlayerId;
  }, [currentClientPlayerId]);

  React.useEffect(() => {
    if (!gameState.id || !currentClientPlayerId) return;
    clearReservedPlayerId(gameState.id);
  }, [currentClientPlayerId, gameState.id]);

  React.useEffect(() => {
    if (currentClientPlayerId) return;
    const sessionPid = getSessionPlayerId();
    if (gameState.players.some((p) => p.id === sessionPid)) {
      log('postJoinAttach', { sessionPid });
      setCurrentClientPlayerId(sessionPid);
    }
  }, [gameState.players, currentClientPlayerId, setCurrentClientPlayerId, log]);

  React.useEffect(() => {
    if (gameState.mode === 'REAL' || currentClientPlayerId) return;
    const token = gameState.groupToken;
    if (!token) return;
    const remembered = loadGroupPlayer(token);
    if (!remembered?.playerId) return;
    if (!gameState.players.some((p) => p.id === remembered.playerId)) return;
    log('groupAttach', { playerId: remembered.playerId });
    try { setSessionPlayerId(remembered.playerId); } catch {}
    setCurrentClientPlayerId(remembered.playerId);
    if (typeof remembered.isHost === 'boolean') setIsHost(remembered.isHost);
  }, [currentClientPlayerId, gameState.groupToken, gameState.mode, gameState.players, setCurrentClientPlayerId, log, setIsHost]);

  React.useEffect(() => {
    if (gameState.mode === 'REAL' || currentClientPlayerId) return;
    const token = gameState.groupToken;
    if (!token) return;
    const remembered = loadGroupPlayer(token);
    if (!remembered?.playerId) return;
    if (!(gameState.spectators || []).some((s) => s.id === remembered.playerId)) return;
    log('groupAttachSpectator', { playerId: remembered.playerId });
    try { setSessionPlayerId(remembered.playerId); } catch {}
    setCurrentClientPlayerId(remembered.playerId);
    setIsHost(false);
  }, [currentClientPlayerId, gameState.groupToken, gameState.mode, gameState.spectators, setCurrentClientPlayerId, log, setIsHost]);

  React.useEffect(() => {
    if (gameState.mode === 'REAL' || !currentClientPlayerId) return;
    const token = gameState.groupToken;
    if (!token) return;
    const remembered = loadGroupPlayer(token);
    if (!remembered?.playerId || remembered.playerId !== currentClientPlayerId) return;
    if (typeof remembered.isHost !== 'boolean') return;
    setIsHost(remembered.isHost);
  }, [currentClientPlayerId, gameState.groupToken, gameState.mode, setIsHost]);

  React.useEffect(() => {
    if (gameState.mode === 'REAL') return;
    if (!isHost) return;
    if (currentClientPlayerId) return;
    if (gameState.lobby?.phase !== 'IN_GAME') return;
    const active = gameState.turnCycle?.activePlayerId;
    if (!active) return;
    log('autoAdoptActivePlayer', { active });
    try { setSessionPlayerId(active); } catch {}
    setCurrentClientPlayerId(active);
  }, [currentClientPlayerId, gameState.lobby?.phase, gameState.mode, gameState.turnCycle?.activePlayerId, isHost, setCurrentClientPlayerId, log]);

  React.useEffect(() => {
    if (gameState.mode === 'REAL') return;
    if (currentClientPlayerId) return;
    if (gameState.lobby?.phase !== 'IN_GAME') return;
    const active = gameState.turnCycle?.activePlayerId;
    if (!active) return;
    const sessionPid = getSessionPlayerId();
    if (sessionPid && sessionPid === active) {
      log('adoptActive:sessionMatch', { active });
      setCurrentClientPlayerId(active);
    }
  }, [currentClientPlayerId, gameState.lobby?.phase, gameState.mode, gameState.turnCycle?.activePlayerId, setCurrentClientPlayerId, log]);

  React.useEffect(() => {
    if (gameState.mode === 'REAL') return;
    if (!gameState.id || currentClientPlayerId) return;
    let sessionPid = getSessionPlayerId();
    if (!sessionPid) {
      try {
        const session = readSession();
        const persistedLastPlayerId = session?.mode === 'ONLINE' && session.gameId?.toUpperCase() === gameState.id.toUpperCase()
          ? session.extras?.lastPlayerId || null
          : null;
        if (persistedLastPlayerId) {
          sessionPid = persistedLastPlayerId;
          setSessionPlayerId(persistedLastPlayerId);
          if (isVerboseLoggingEnabled()) {
            logger.debug('RECOVERY_USING_SESSION_PLAYER', { fallback: persistedLastPlayerId.substring(0, 8) });
          }
        }
      } catch {}
    }
    if (!sessionPid) {
      try {
        const fallback = localStorage.getItem(`dice1k_last_player_id_${gameState.id}`);
        if (fallback) {
          sessionPid = fallback;
          setSessionPlayerId(fallback);
          if (isVerboseLoggingEnabled()) {
            logger.debug('RECOVERY_USING_FALLBACK_PLAYER', { fallback: fallback.substring(0, 8) });
          }
        }
      } catch {}
    }
    if (!sessionPid) return;
    const debug = (msg: string, extra?: unknown) => {
      if (!isVerboseLoggingEnabled()) return;
      logger.debug(`RECOVERY_${msg}`, extra);
    };
    if (gameState.players.some((p) => p.id === sessionPid)) {
      debug('Reattaching existing player id', { sessionPid });
      setCurrentClientPlayerId(sessionPid);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        debug('Fetching snapshot state for recovery', { roomId: gameState.id });
        const restored = await eventStore.getGameState(gameState.id);
        if (cancelled || !restored) return;
        if (restored.players.some((p) => p.id === sessionPid)) {
          debug('Player found in snapshot/lobby state', { sessionPid });
          setCurrentClientPlayerId(sessionPid);
        } else {
          debug('Player not found in snapshot/lobby state', { sessionPid });
        }
      } catch (e) {
        debug('Recovery failed', { error: e instanceof Error ? e.message : String(e) });
      }
    })();
    return () => { cancelled = true; };
  }, [currentClientPlayerId, gameState.id, gameState.mode, gameState.players, setCurrentClientPlayerId]);

  React.useEffect(() => {
    const debugAdopt = typeof window !== 'undefined' && (window as RuntimeWindow).__DEBUG_ADOPT_ALL_PLAYERS__ === true;
    if (!debugAdopt) return;
    if (gameState.mode === 'REAL') return;
    const active = gameState.turnCycle?.activePlayerId;
    if (!active || currentClientPlayerId === active) return;
    log('debugAdoptActive', { active });
    try { setSessionPlayerId(active); } catch {}
    setCurrentClientPlayerId(active);
  }, [currentClientPlayerId, gameState.mode, gameState.turnCycle?.activePlayerId, setCurrentClientPlayerId, gameState.turnCycle, log]);

  React.useEffect(() => {
    if (currentClientPlayerId) return;
    const sessionPid = getSessionPlayerId();
    if (!sessionPid) return;
    if (gameState.players.some((p) => p.id === sessionPid)) {
      log('lateAttach', { sessionPid });
      setCurrentClientPlayerId(sessionPid);
    }
  }, [currentClientPlayerId, gameState.players, setCurrentClientPlayerId, log]);
}
